import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  startActiveEditing,
  stopActiveEditing,
} from "../../actions/activeEditingActions";

/**
 * Registers/unregisters an active editing session in the global Redux counter.
 * While any component holds an active editing session, AutoSave components will
 * defer their PATCH calls to avoid triggering re-renders (e.g. closing an open
 * date picker) caused by background reload responses.
 *
 * Usage:
 *   useActiveEditing(isEditing);
 *
 * For modals that always have an active form, simply pass `true`.
 */
export default function useActiveEditing(isEditing) {
  const dispatch = useDispatch();
  const prevIsEditing = useRef(false);

  useEffect(() => {
    if (isEditing && !prevIsEditing.current) {
      dispatch(startActiveEditing());
    } else if (!isEditing && prevIsEditing.current) {
      dispatch(stopActiveEditing());
    }
    prevIsEditing.current = isEditing;
  }, [isEditing, dispatch]);

  // Release the lock if the component unmounts while still editing.
  useEffect(() => {
    return () => {
      if (prevIsEditing.current) {
        dispatch(stopActiveEditing());
      }
    };
  }, [dispatch]);
}
